import React from "react";
import { DataTable, ColumnDef } from "../ui/data-table";
import { CorpusItem } from "@/interfaces/corpus.interface";
import { CorpusStatus } from "@/constants/corpus.constants";
import { Button } from "../ui/button";
import { ExternalLink, RotateCw } from "lucide-react";
import { formatDate } from "@/utils/date.utils";

/**
 * Props for the CorpusTable component
 */
interface CorpusTableProps {
  /** Array of corpus items to display */
  data: CorpusItem[];
  /** Pagination information */
  pagination?: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  /** Current sort key */
  sortBy?: string;
  /** Current sort order */
  sortOrder?: "asc" | "desc" | null;
  /** Callback fired when sort changes */
  onSortChange?: (key: string, order: "asc" | "desc" | null) => void;
  /** Callback fired when page changes */
  onPageChange?: (page: number) => void;
  /** Callback fired when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Whether table is loading */
  isLoading?: boolean;
  /** Callback fired when document view is requested */
  onViewDocument?: (item: CorpusItem) => void;
  /** Callback fired when retry is requested */
  onRetry?: (item: CorpusItem) => void;
  /** Set of item IDs currently retrying */
  retryingItems?: Set<number>;
}

/**
 * CorpusTable component displays corpus items in a data table with sorting,
 * pagination, status indicators, and action buttons (view, retry).
 * Uses DataTable component with custom column definitions.
 * 
 * @component
 * @param {CorpusTableProps} props - Component props
 * @param {CorpusItem[]} props.data - Corpus items to display
 * @param {Object} [props.pagination] - Pagination info
 * @param {string} [props.sortBy] - Current sort key
 * @param {"asc" | "desc" | null} [props.sortOrder] - Current sort order
 * @param {(key: string, order: "asc" | "desc" | null) => void} [props.onSortChange] - Sort change callback
 * @param {(page: number) => void} [props.onPageChange] - Page change callback
 * @param {(pageSize: number) => void} [props.onPageSizeChange] - Page size change callback
 * @param {boolean} [props.isLoading] - Whether loading
 * @param {(item: CorpusItem) => void} [props.onViewDocument] - View document callback
 * @param {(item: CorpusItem) => void} [props.onRetry] - Retry callback
 * @param {Set<number>} [props.retryingItems] - Retrying item IDs
 * @returns {JSX.Element} Data table with corpus items
 * 
 * @example
 * ```tsx
 * <CorpusTable
 *   data={corpusItems}
 *   pagination={pagination}
 *   onSortChange={handleSort}
 *   onViewDocument={handleView}
 * />
 * ```
 */

/**
 * Format file size from bytes to human-readable format
 */
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "—";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB";
};

const getStatusDisplay = (status: string) => {
  const statusUpper = status.toUpperCase();
  
  if (statusUpper === "SUCCESS" || statusUpper === CorpusStatus.SUCCESS.toUpperCase()) {
    return {
      text: CorpusStatus.SUCCESS,
      className: "text-green-600 font-medium",
    };
  }
  
  if (statusUpper === "INPROGRESS" || statusUpper === "IN PROGRESS" || statusUpper === CorpusStatus.IN_PROGRESS.toUpperCase()) {
    return {
      text: CorpusStatus.IN_PROGRESS,
      className: "text-orange-600 font-medium",
    };
  }
  
  if (statusUpper === "FAILED" || statusUpper === CorpusStatus.FAILED.toUpperCase()) {
    return {
      text: CorpusStatus.FAILED,
      className: "text-red-600 font-medium",
    };
  }
  
  return {
    text: status,
    className: "text-muted-foreground",
  };
};

/**
 * Render file type/source cell
 */
const renderFileTypeSource = (item: CorpusItem): React.ReactNode => {
  const fileType = item.fileType || "";
  const fileSize = item.fileSize;
  const formattedSize = formatFileSize(fileSize);
  
  return (
    <span className="text-foreground">
      {fileType} ({formattedSize})
    </span>
  );
};

/**
 * Render ingestion status cell
 */
const renderIngestionStatus = (status: string): React.ReactNode => {
  const { text, className } = getStatusDisplay(status);
  return <span className={className}>{text}</span>;
};

/**
 * Render actions cell
 */
const renderActions = (
  item: CorpusItem,
  onViewDocument?: (item: CorpusItem) => void,
  onRetry?: (item: CorpusItem) => void,
  retryingItems?: Set<number>
): React.ReactNode => {
  const statusUpper = item.ingestionStatus.toUpperCase();
  // Handle backend values: "SUCCESS", "FAILED", "INPROGRESS"
  const isSuccess = statusUpper === "SUCCESS" || statusUpper === CorpusStatus.SUCCESS.toUpperCase();
  const isInProgress =
    statusUpper === "INPROGRESS" ||
    statusUpper === "IN PROGRESS" ||
    statusUpper === CorpusStatus.IN_PROGRESS.toUpperCase();
  const isFailed = statusUpper === "FAILED" || statusUpper === CorpusStatus.FAILED.toUpperCase();
  const isRetrying = retryingItems?.has(item.id) ?? false;

  if (isSuccess) {
    return (
      <button
        onClick={() => onViewDocument?.(item)}
        className="text-blue-600 hover:text-blue-800 transition-colors"
        aria-label="View document"
      >
        <ExternalLink className="h-4 w-4" />
      </button>
    );
  }

  if (isInProgress) {
    return (
      <button
        onClick={() => onViewDocument?.(item)}
        className="text-gray-400 cursor-not-allowed"
        aria-label="Document in progress"
        disabled
      >
        <ExternalLink className="h-4 w-4" />
      </button>
    );
  }

  if (isFailed) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onRetry?.(item)}
        disabled={isRetrying}
        className="text-red-600 hover:text-red-800 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <RotateCw className={`h-4 w-4 mr-1 ${isRetrying ? 'animate-spin' : ''}`} />
        <span>Retry</span>
      </Button>
    );
  }

  return <span className="text-muted-foreground">—</span>;
};

const renderFileName = (filename:string) => {
  if (filename.length > 60) {
    return <span className="cursor-default" title={filename}>{filename.substring(0, 50) + '...'}</span>;
  } else {
    return <span className="cursor-default" title={filename}>{filename}</span>;
  }
}

/**
 * Corpus Table Component
 * 
 * Displays corpus data in a table format with:
 * - Corpus Name
 * - File Type/Source (with file size)
 * - Ingestion Status (color-coded)
 * - Ingestion Time (formatted)
 * - Actions (view link or retry button based on status)
 */
export const CorpusTable: React.FC<CorpusTableProps> = ({
  data,
  pagination,
  sortBy,
  sortOrder,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  onViewDocument,
  onRetry,
  retryingItems,
}) => {
  const columns: ColumnDef<CorpusItem>[] = [
    {
      id: "corpusName",
      header: "Corpus Name",
      accessor: (row) => row.corpusName,
      render: (name) => <span className="capitalize">{name}</span>,
      sortable: true,
      headerClassName: "min-w-[150px]",
    },
    {
      id: "fileName",
      header: "File Name",
      accessor: (row) => row.fileName,
      render: (filename, row) => renderFileName(filename.split(`.${row.fileType.toLowerCase()}`)[0]),
    },
    {
      id: "fileTypeSource",
      header: "File Type/Source",
      accessor: (row) => row,
      render: renderFileTypeSource,
      sortable: false,
      headerClassName: "min-w-[180px]",
    },
    {
      id: "ingestionStatus",
      header: "Ingestion Status",
      accessor: (row) => row.ingestionStatus,
      render: (status) => renderIngestionStatus(status),
      sortable: true,
      headerClassName: "min-w-[140px]",
    },
    {
      id: "ingestionTime",
      header: "Ingestion Time",
      accessor: (row) => row.ingestionTime,
      render: (time) => formatDate(time),
      sortable: true,
      headerClassName: "min-w-[200px]",
    },
    {
      id: "actions",
      header: "Actions",
      accessor: (row) => row,
      render: (item) => renderActions(item, onViewDocument, onRetry, retryingItems),
      sortable: false,
      headerClassName: "min-w-[120px]",
      cellClassName: "flex items-center justify-center",
    },
  ];

  return (
    <DataTable
      data={data}
      columns={columns}
      pagination={pagination}
      sortKey={sortBy}
      sortOrder={sortOrder}
      onSortChange={onSortChange}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      isLoading={isLoading}
      emptyMessage="No corpus data available"
      getRowKey={(row) => row.id}
    />
  );
};

